import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    Shield,
    LogOut,
    Sparkles,
    DollarSign,
    Briefcase,
    ShieldAlert,
    MessageSquareWarning,
    Activity
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';

const AdminSidebar = ({ onClose }) => {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const handleNavClick = (path) => {
        navigate(path);
        onClose && onClose();
    };

    const menuItems = [
        {
            icon: LayoutDashboard,
            label: 'Dashboard',
            path: '/admin',
            gradient: 'from-red-600 to-orange-600'
        },
        { type: 'divider' },
        {
            icon: Users,
            label: 'Usuários',
            path: '/admin/users',
            gradient: 'from-blue-600 to-indigo-600'
        },
        {
            icon: Briefcase,
            label: 'Prestadores',
            path: '/admin/providers',
            gradient: 'from-cyan-600 to-blue-600'
        },
        { type: 'divider' },
        {
            icon: DollarSign,
            label: 'Pagamentos',
            path: '/admin/payments',
            gradient: 'from-green-600 to-emerald-600'
        },

        { type: 'divider' },
        {
            icon: Sparkles,
            label: 'Funções',
            path: '/admin/functions',
            gradient: 'from-purple-600 to-pink-600'
        },
        { type: 'divider' },
        {
            icon: MessageSquareWarning,
            label: 'Feedbacks',
            path: '/admin/feedbacks',
            gradient: 'from-amber-500 to-orange-500'
        },
        {
            icon: ShieldAlert,
            label: 'BlackList',
            path: '/admin/blacklist',
            gradient: 'from-red-600 to-rose-600'
        },
        {
            icon: Activity,
            label: 'Atividade',
            path: '/admin/activity',
            gradient: 'from-teal-500 to-lime-500'
        },
        { type: 'divider' },
        {
            icon: Shield,
            label: 'Painel da Staff',
            path: '/staff',
            gradient: 'from-red-600 to-orange-600'
        },
        {
            icon: Users,
            label: 'Painel do Usuário',
            path: '/dashboard/user',
            gradient: 'from-blue-500 to-cyan-500'
        }
    ];

    return (
        <div
            className="w-64 bg-gray-950/90 backdrop-blur-xl border-r border-gray-800/50 flex flex-col h-full lg:fixed lg:left-0 z-50 transition-all duration-300"
            style={{
                top: 'var(--banner-height, 0px)',
                height: 'calc(100vh - var(--banner-height, 0px))'
            }}
        >
            {/* Header com gradiente */}
            <div className="p-4 lg:p-6 border-b border-gray-800/50 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 via-orange-600/10 to-yellow-600/10" />
                <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-1">
                        <h1 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                            Admin Panel
                        </h1>
                        <Shield className="h-4 w-4 lg:h-5 lg:w-5 text-red-500 animate-pulse" />
                    </div>
                    <p className="text-xs text-gray-400 font-medium">Controle Total do Sistema</p>
                </div>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 overflow-y-auto py-4 lg:py-6 px-3 lg:px-4 space-y-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
                {menuItems.map((item, index) => (
                    item.type === 'divider' ? (
                        <div key={`divider-${index}`} className="my-2 border-t border-gray-800/50" />
                    ) : (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === '/admin'}
                            onClick={() => onClose && onClose()}
                            className={({ isActive }) =>
                                `group flex items-center space-x-3 px-3 lg:px-4 py-3 lg:py-3.5 rounded-xl transition-all duration-300 relative overflow-hidden ${isActive
                                    ? 'bg-gray-800/50 text-white border border-gray-700/50 shadow-lg'
                                    : 'text-gray-400 hover:text-white hover:bg-gray-900/30'
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {isActive && (
                                        <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-10`} />
                                    )}

                                    <div className={`relative z-10 p-1.5 lg:p-2 rounded-lg transition-all duration-300 ${isActive
                                        ? `bg-gradient-to-br ${item.gradient}`
                                        : 'bg-gray-800/50 group-hover:bg-gray-800'
                                        }`}>
                                        {item.icon && <item.icon className={`h-4 w-4 lg:h-5 lg:w-5 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} />}
                                    </div>

                                    <span className={`font-medium text-sm lg:text-base transition-colors relative z-10 ${isActive ? 'text-white' : 'group-hover:text-white'
                                        }`}>
                                        {item.label}
                                    </span>

                                    {isActive && (
                                        <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 lg:h-8 rounded-l-full bg-gradient-to-b ${item.gradient}`} />
                                    )}
                                </>
                            )}
                        </NavLink>
                    )
                ))}
            </nav>

            {/* Botão de Logout com gradiente */}
            <div className="p-3 lg:p-4 border-t border-gray-800/50">
                <Button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center space-x-3 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white border-0 shadow-lg shadow-red-500/20 transition-all duration-300 h-10 lg:h-11"
                >
                    <LogOut className="h-4 w-4 lg:h-5 lg:w-5" />
                    <span className="font-medium text-sm lg:text-base">Sair</span>
                </Button>
            </div>
        </div>
    );
};

export default AdminSidebar;
